"use client";

import { useDict } from "@/components/i18n/DictProvider";
import Link from "next/link";
import { ArrowRight, Lock, LogIn } from "lucide-react";
import { langPath, type Lang } from "@/lib/i18n";
import { formatPrice } from "@/lib/format";
import type { ModuleAccessLevelEnum } from "@/lib/lecturehub/types";
import { paywallVariant } from "./paywall";

/**
 * Locked-content slot (player area, episode list, etc.).
 *
 * The component encodes the full barrier matrix and picks the right variant per
 * module + viewer. Callers render PaywallCard whenever the underlying content
 * can't show (typically `Module.canView === false`); PaywallCard decides what.
 *
 * WHAT UNLOCKS IT IS `accessLevel`. `Module.free` is OBSOLETE and is not read
 * here at all. On a single-payment portal the ladder means: PUBLIC = open to
 * everyone, REGISTERED = enrolment only, PREMIUM = payable OR enrolment. On a
 * subscription portal PREMIUM means the subscription instead.
 *
 * The matrix itself lives in ./paywall.ts (pure + unit-tested); this component
 * only renders the variant it returns:
 *
 *   login      → "Log in to access" → /login. Every rung above PUBLIC needs an
 *                account first, and a blocked PUBLIC module is user-group
 *                restricted, which signing in may clear.
 *   none       → null. The unlock is an enrolment (the caller's enroll button
 *                owns that CTA), or the ladder is not what is blocking here.
 *   buy        → "Buy €X" → checkoutHref (the /checkout route — embedded Stripe
 *                Elements over addToCart → checkoutCart). Falls back to a
 *                disabled placeholder when no checkoutHref is passed.
 *   subscribe  → "Subscribe to unlock" → /profil/narocnine.
 *
 * There is deliberately NO "currently unavailable" state any more: a priceless
 * PREMIUM module on a single-payment portal is enrol-to-watch, not a dead end.
 *
 * Returning `null` for the no-op cases means callers need no pre-check guards.
 */
export function PaywallCard({
  lang,
  aspect = "16/9",
  className,
  accessLevel,
  isAuthed,
  price,
  checkoutHref,
  isSubscription,
  hideCta = false,
}: {
  lang: Lang;
  aspect?: string;
  className?: string;
  /** Render the explanatory card (eyebrow + title + body) WITHOUT its action
   *  button — for when the actual CTA lives elsewhere (e.g. the MediaSidebar on
   *  podcast/ott detail), so the body just explains the gate. */
  hideCta?: boolean;
  /** `Module.accessLevel` — who may WATCH, and the whole basis for the CTA. */
  accessLevel?: ModuleAccessLevelEnum | null;
  /** Whether the current viewer is logged in. */
  isAuthed: boolean;
  /** `Module.price` in cents (Stripe-native unit, matches the rest of the
   *  schema's *.price fields). Drives payper vs subscription variant. */
  price?: number | null;
  /** Where to send the user when they click the payper "Buy" CTA. Required
   *  when `price > 0`; optional otherwise. Callers compute this because the
   *  paywall doesn't know the module's slug. */
  checkoutHref?: string;
  /** Portal runs in subscription mode (`portal.paymentModel`). Decides the
   *  no-price fallback: subscribe (subscription) vs coming-soon (single). */
  isSubscription: boolean;
}) {
  const t = useDict();
  const pt = t.portal;

  // The matrix lives in lib/paywall.ts (pure + unit-tested); this component only
  // renders the variant it returns.
  const variant = paywallVariant({ isAuthed, accessLevel, price, isSubscription });

  if (variant === "none") return null;

  // Auth gate. Even free content requires an account.
  if (variant === "login") {
    return (
      <Shell aspect={aspect} className={className}>
        <Eyebrow text={pt.authGateEyebrow} />
        <Title>{pt.authGateTitle}</Title>
        <Body>
          {/* Translatable template with {login}/{register} link slots — the
              linked words are their own keys so translators control both the
              prose and the anchor text. */}
          {pt.authGateBodyTpl.split(/(\{login\}|\{register\})/).map((part, i) =>
            part === "{login}" ? (
              <PaywallTextLink key={i} href={langPath(lang, "/login")}>
                {pt.authGateLoginWord}
              </PaywallTextLink>
            ) : part === "{register}" ? (
              <PaywallTextLink key={i} href={langPath(lang, "/register")}>
                {pt.authGateRegisterWord}
              </PaywallTextLink>
            ) : (
              part
            ),
          )}
        </Body>
        {!hideCta ? (
          <PrimaryLink href={langPath(lang, "/login")} icon="login">
            {pt.authGateCta}
          </PrimaryLink>
        ) : null}
      </Shell>
    );
  }

  // Per-module price set → payper-style "Buy €X". With a checkoutHref, send the
  // user to the checkout route (embedded Stripe Elements over addToCart →
  // checkoutCart); without one (e.g. OTT/podcast callers not wired to a checkout
  // route yet), fall back to the disabled placeholder so the price still shows.
  if (variant === "buy") {
    // The picker only returns "buy" when price > 0; this keeps the narrowing
    // local instead of re-testing the condition it already decided.
    const amount = price ?? 0;
    return (
      <Shell aspect={aspect} className={className}>
        <Eyebrow text={t.detail.paidCourseEyebrow} />
        <Title>{t.detail.paywallTitle}</Title>
        <Body>{checkoutHref ? t.detail.paywallBodySingle : pt.payperComingSoon}</Body>
        {hideCta ? null : checkoutHref ? (
          <PrimaryLink href={checkoutHref} icon="arrow">
            {pt.payperBuyLabel} {formatPrice(amount)}
          </PrimaryLink>
        ) : (
          <button
            type="button"
            disabled
            className="mt-6 inline-flex items-center justify-center gap-2 h-11 px-5 rounded-full bg-accent/40 text-primary/70 font-display font-bold uppercase tracking-[0.12em] text-[12px] cursor-not-allowed"
            title={pt.payperComingSoon}
          >
            {pt.payperBuyLabel} {formatPrice(amount)}
            <Lock className="size-3.5" aria-hidden />
          </button>
        )}
      </Shell>
    );
  }

  // PREMIUM on a subscription portal → "Subscribe to unlock". This now fires for
  // a FREE premium module too, which is the fix: the price is irrelevant when
  // the gate is the subscription.
  if (variant === "subscribe") {
    return (
      <Shell aspect={aspect} className={className}>
        <Eyebrow text={t.detail.membersOnlyEyebrow} />
        <Title>{t.detail.paywallTitle}</Title>
        <Body>{t.detail.paywallBody}</Body>
        {!hideCta ? (
          <PrimaryLink href={langPath(lang, "/profil/narocnine")} icon="arrow">
            {t.detail.subscribe}
          </PrimaryLink>
        ) : null}
      </Shell>
    );
  }

  // Exhaustive: paywallVariant only returns the four variants above.
  return null;
}

/* ────────── presentational helpers (shared dark-navy chrome) ────────── */

function Shell({
  aspect,
  className,
  children,
}: {
  aspect: string;
  className?: string;
  children: React.ReactNode;
}) {
  return (
    <div
      className={
        "relative overflow-hidden grid place-items-center text-center px-6 py-10 md:py-14 text-primary-foreground rounded-card " +
        (className ?? "")
      }
      style={{
        aspectRatio: aspect,
        background:
          "linear-gradient(150deg, color-mix(in srgb, var(--color-primary), black 10%) 0%, color-mix(in hsl, var(--color-primary), white 5%) 60%, var(--color-primary) 100%)",
        boxShadow: "inset 0 0 0 1px color-mix(in srgb, var(--color-accent) 25%, transparent)",
      }}
    >
      <div
        aria-hidden
        className="absolute -top-20 -right-12 size-56 rounded-full bg-accent/20 blur-3xl"
      />
      <div
        aria-hidden
        className="absolute -bottom-20 -left-12 size-56 rounded-full bg-primary-foreground/[0.04] blur-3xl"
      />
      <div className="relative max-w-md mx-auto">
        <span
          aria-hidden
          className="inline-flex size-11 items-center justify-center rounded-full bg-accent/15 text-accent ring-1 ring-inset ring-accent/30 mb-4"
        >
          <Lock className="size-5" strokeWidth={2} />
        </span>
        {children}
      </div>
    </div>
  );
}

function Eyebrow({ text }: { text: string }) {
  return (
    <p className="font-display font-extrabold uppercase tracking-[0.22em] text-[10.5px] text-accent">
      {text}
    </p>
  );
}

function Title({ children }: { children: React.ReactNode }) {
  return (
    <h3 className="mt-2 font-display font-semibold text-[20px] md:text-[22px] !text-primary-foreground !leading-tight">
      {children}
    </h3>
  );
}

function Body({ children }: { children: React.ReactNode }) {
  return (
    <p className="mt-3 text-[13.5px] md:text-[14px] leading-relaxed text-primary-foreground/70">
      {children}
    </p>
  );
}

/** Inline text link inside a paywall body — styled to read on the dark card. */
function PaywallTextLink({ href, children }: { href: string; children: React.ReactNode }) {
  return (
    <Link
      href={href}
      className="font-semibold text-accent underline underline-offset-2 transition-colors hover:text-accent/80"
    >
      {children}
    </Link>
  );
}

function PrimaryLink({
  href,
  icon,
  children,
}: {
  href: string;
  icon: "arrow" | "login";
  children: React.ReactNode;
}) {
  const Icon = icon === "login" ? LogIn : ArrowRight;
  return (
    <Link
      href={href}
      className="mt-6 inline-flex items-center justify-center gap-2 h-11 px-5 rounded-full bg-accent text-accent-foreground font-display font-bold uppercase tracking-[0.12em] text-[12px] transition-all duration-200 ease-out hover:-translate-y-0.5 hover:brightness-105 active:translate-y-0 active:brightness-100 no-underline"
      style={{
        boxShadow:
          "0 2px 4px rgb(0 0 0 / 0.25), 0 8px 18px -6px color-mix(in srgb, color-mix(in srgb, var(--color-accent), black 45%) 55%, transparent), inset 0 1px 0 color-mix(in srgb, color-mix(in srgb, var(--color-accent), white 80%) 60%, transparent), inset 0 -1px 0 color-mix(in srgb, color-mix(in srgb, var(--color-accent), black 40%) 20%, transparent)",
      }}
    >
      {children}
      <Icon className="size-3.5" aria-hidden />
    </Link>
  );
}
